import {
  getStandardAnalysis,
  openMarimoResult,
  prepareStandardAnalysisInteraction,
} from '@/api/inspiration'

// 标准分析结果页预热：在用户点开结果之前，先把结果应用准备好、把 Notebook 运行环境起起来。
// 运行环境就绪后 xBIOS 会在服务端预跑整页结果（marimo-prewarm.ts），用户随后打开时直接接管，
// 不用再等起环境和逐格执行。纯优化：任何失败都静默放弃，打开结果页仍走原来的完整流程。
//
// 资源约束：只预热「当前这一个」分析——同一时刻最多一个预热在途，同一运行 25 分钟内不重复预热
// （运行环境无人连接时保留 30 分钟，见 xBIOS MARIMO_DISCONNECT_RETENTION_MS）。

const REPEAT_WINDOW_MS = 25 * 60 * 1000
const STORAGE_KEY = 'gutdb-result-prewarm'
let inflight = null

const recentlyPrewarmed = () => {
  try {
    const value = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}')
    return value && typeof value === 'object' ? value : {}
  } catch {
    return {}
  }
}

const remember = key => {
  const now = Date.now()
  const entries = Object.entries(recentlyPrewarmed()).filter(([, at]) => now - at < REPEAT_WINDOW_MS)
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries([...entries, [key, now]])))
  } catch {
    // sessionStorage 不可用时退化为只靠 inflight 去重
  }
}

const prewarmedRecently = key => Date.now() - Number(recentlyPrewarmed()[key] || 0) < REPEAT_WINDOW_MS

// 正打开着的结果页会自己起 Notebook；这些分析不再预热，免得同一 Notebook 两路并发启动。
const openResultPages = new Map()

/** 结果页显示期间调用，返回释放函数；同一分析可被多处持有。 */
export const holdResultNotebookPrewarm = analysisId => {
  if (!analysisId) return () => {}
  openResultPages.set(analysisId, (openResultPages.get(analysisId) || 0) + 1)
  let released = false
  return () => {
    if (released) return
    released = true
    const count = (openResultPages.get(analysisId) || 0) - 1
    if (count > 0) openResultPages.set(analysisId, count)
    else openResultPages.delete(analysisId)
  }
}

/** 用户自己打开结果页时调用：前端不再等预热，服务端已经起步的运行环境留给这次打开。 */
export const cancelResultNotebookPrewarm = () => {
  inflight?.controller.abort()
  inflight = null
}

/** 预热一个分析的结果页；runId 省略时取该分析当前运行。 */
export const prewarmResultNotebook = async (analysisId, runId = '') => {
  if (!analysisId || inflight || openResultPages.has(analysisId) || !localStorage.getItem('token')) return false
  if (runId && prewarmedRecently(runId)) return false
  const controller = new AbortController()
  const task = { controller }
  inflight = task
  try {
    const response = await getStandardAnalysis(analysisId, { signal: controller.signal })
    const analysis = response.data?.data
    const run = runId
      ? (analysis?.runs || []).find(item => item.runId === runId) || (analysis?.currentRun?.runId === runId ? analysis.currentRun : null)
      : analysis?.currentRun
    if (!run?.runId || run.executionStatus !== 'COMPLETED' || !run.marimoPath) return false
    if (prewarmedRecently(run.runId)) return false
    remember(run.runId)
    const prepared = await prepareStandardAnalysisInteraction(analysisId, run.runId, { signal: controller.signal })
    if (prepared.data?.state !== 'READY' || prepared.data.marimoPath !== run.marimoPath) return false
    if (controller.signal.aborted || openResultPages.has(analysisId)) return false
    await openMarimoResult(run.marimoPath, {
      standardResult: { analysisId, runId: run.runId },
      signal: controller.signal,
      restart: false,
      autoRestartEnded: true,
      timeoutMs: 5 * 60 * 1000,
    })
    return true
  } catch {
    return false
  } finally {
    if (inflight === task) inflight = null
  }
}

/** 进入分析列表类页面时：预热最近更新的一个已完成分析。 */
export const prewarmLatestCompletedResult = records => {
  const latest = (records || [])
    .filter(item => item?.analysisId && item.executionStatus === 'COMPLETED')
    .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')))[0]
  if (!latest || inflight || prewarmedRecently(`analysis:${latest.analysisId}`)) return
  remember(`analysis:${latest.analysisId}`)
  void prewarmResultNotebook(latest.analysisId)
}

/** 收到「标准分析已完成」消息时：预热那一次运行。 */
export const prewarmFinishedAnalysisMessages = (messages, afterId) => {
  const finished = (messages || [])
    .filter(item => Number(item?.id) > afterId &&
      item.eventType === 'ANALYSIS_FINISHED' &&
      item.content?.outcome === 'COMPLETED' &&
      item.target?.analysisId && item.target?.runId)
    .sort((a, b) => Number(b.id) - Number(a.id))[0]
  if (finished) void prewarmResultNotebook(finished.target.analysisId, finished.target.runId)
}
